import * as R from 'ramda';
import * as yup from 'yup';
import i18n from 'helpers/i18n';
import messages from './messages';
import { useState } from 'react';
import { Box } from 'components/Box';
import {
  MetaDataOption,
  MetaDataOptionProps
} from '../MetaDataMultiSelect/MetaDataOption';
import { CycleMetaDataValue } from 'types/api/envScales/CycleMetaDataValue';
import { ObservationCycle } from 'types/api/envScales/ObservationCycle';
import { Assessment } from 'types/api/envScales/Assessment';
import { AgeLevel } from 'types/api/envScales/AgeLevel';
import { updateCycleMetaData } from 'actions/envScales/observationCycles';
import {
  getInitialValues,
  getPayloadValues,
  SelectedValues
} from '../MetaDataMultiSelect/utils';
import { ErrorMessage } from 'components/Measure/View/Styled';
import useDebouncedEffect from 'hooks/useDebouncedEffect';
import { useServerFieldError } from 'components/Measure/View/ValidationsContext';
import ASIMetaData from './ASIMetaData';

interface ITMetaDataContentProps {
  /** Observation Cycle. */
  cycle: ObservationCycle;

  /** CLASS Assessment. */
  assessment: Assessment;

  /** Environmental Scales Age Level. */
  ageLevel: AgeLevel;

  /** Observation ID */
  observationId: number;

  /** Meta data values */
  values: CycleMetaDataValue[];
}

function ITMetaDataContent({
  assessment,
  cycle,
  ageLevel,
  observationId,
  values: initialValues
}: ITMetaDataContentProps) {
  const [touched, setTouched] = useState(false);

  const contentCategory = ageLevel.meta_data_categories?.find(
    category => category.t_name === 'content'
  );

  const contentQuestions =
    contentCategory?.choices?.map(choice => ({
      choiceId: choice.id,
      question: choice.meta_data_questions[0]?.question || '',
      questionId: choice.meta_data_questions[0]?.id
    })) || [];

  const [values, setValues] = useState(
    contentCategory ? getInitialValues(contentCategory, initialValues) : {}
  );

  const checkedChoiceNames = Object.keys(values).filter(
    name => values[name]?.isChecked
  );
  const primaryChoiceNames = Object.keys(values).filter(
    name => values[name]?.isPrimary
  );
  const contentCategoryName = contentCategory?.t_name ?? 'content';

  const contentError = useServerFieldError(
    `cycles.${cycle.cycle_number}.details.${contentCategoryName}.error`,
    checkedChoiceNames,
    yup.array().min(1)
  );
  const contentPrimaryError = useServerFieldError(
    `cycles.${cycle.cycle_number}.details.${contentCategoryName}.primary_error`,
    primaryChoiceNames,
    yup.array().min(1)
  );
  const contentErrorMessage = [
    contentError?.message,
    contentPrimaryError?.message
  ]
    .filter(Boolean)
    .join(' ');

  const handleDebouncedUpdate = (
    touched: boolean,
    values: SelectedValues,
    categoryId: number
  ) => {
    if (!touched) {
      return;
    }

    const payload = {
      meta_data: {
        meta_data_category_id: categoryId,
        values: getPayloadValues(values)
      }
    };

    updateCycleMetaData(observationId, cycle.id, payload);
  };

  useDebouncedEffect(
    () =>
      contentCategory &&
      handleDebouncedUpdate(touched, values, contentCategory.id),
    1000,
    [values, touched]
  );

  const handleChange: MetaDataOptionProps['onChange'] = data => {
    let newValues = { ...values };
    const isNewPrimary = data.value.isPrimary;

    if (isNewPrimary) {
      newValues = R.mapObjIndexed(
        value => ({ ...value, isPrimary: false }),
        newValues
      );
    }

    const mergedValues = {
      ...newValues,
      [data.name]: {
        ...data.value,
        isChecked: data.value.isChecked ?? false,
        id: data.value.choiceId
      }
    };

    setValues(mergedValues);
    setTouched(true);
  };

  const selectedContentChoices =
    contentCategory?.choices?.filter(
      choice => values[choice.t_name]?.isChecked
    ) || [];

  if (
    !contentCategory ||
    !contentCategory.choices ||
    contentCategory.choices.length === 0
  ) {
    return null;
  }

  return (
    <>
      <Box mt="50px">
        <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-blue-600">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {contentCategory.name}
          </h3>
          <p className="text-gray-600 mb-4">{contentCategory.description}</p>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {contentCategory.choices.map(choice => (
              <MetaDataOption
                key={choice.id}
                choice={choice}
                value={
                  values[choice.t_name] || {
                    isChecked: false,
                    isPrimary: false
                  }
                }
                onChange={handleChange}
              />
            ))}
          </div>

          {contentErrorMessage && (
            <ErrorMessage>{contentErrorMessage}</ErrorMessage>
          )}
        </div>
      </Box>

      {selectedContentChoices.map(choice => {
        const contentQuestion = contentQuestions.find(
          q => q.choiceId === choice.id
        );
        return (
          <ASIMetaData
            key={choice.id}
            assessment={assessment}
            cycle={cycle}
            ageLevel={ageLevel}
            observationId={observationId}
            values={initialValues}
            contentChoiceId={choice.id}
            contentChoiceName={choice.t_name}
            headerTitle={i18n.ft(messages.dataTitle, { value: choice.name })}
            question={i18n.ft(messages.dataQuestion, { value: choice.name })}
            contentQuestion={i18n.ft(messages.dataContentQuestion, {
              value: choice.name
            })}
            formatQuestion={i18n.ft(messages.dataFormatQuestion, {
              value: choice.name
            })}
            contentExposureQuestion={contentQuestion?.question || ''}
            contentExposureQuestionId={contentQuestion?.questionId}
          />
        );
      })}
    </>
  );
}

export default ITMetaDataContent;
